import React, { useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { DetailBaseContext } from '@dashboard/lib/detailLinks';

/**
 * An order, subscription, submission or customer opened from one of a page's lists, shown inside
 * the page so its sidebar stays. The back link returns to Summary or the record's own list, or to
 * the record that opened it when that one says so (`state.back`: a booking opening an attendee).
 */
export default function PageDetail({ pagePath, listPath, listLabel, children }) {
  const { state } = useLocation();
  // Kept across the detail's own tabs, whose links carry no state.
  const openedFrom = useRef(state?.from);
  if (state?.from) openedFrom.current = state.from;
  const openedBack = useRef(state?.back);
  if (state?.back) openedBack.current = state.back;
  // Back to Summary, or to the list with its filters, when opened from there. From anywhere else
  // (another detail) it goes to its own list, so two details never send you back and forth.
  const fromPath = openedFrom.current?.split('?')[0];
  const fromSummary = fromPath === `${pagePath}/overview`;
  const back = openedBack.current;
  const backTo = back
    ? back.to
    : fromSummary || fromPath === listPath
      ? openedFrom.current
      : listPath;
  const backLabel = back ? back.label : fromSummary ? 'Summary' : listLabel;

  return (
    // The detail's own lists resolve their links with usePageRecordHref, not the list's base.
    <DetailBaseContext.Provider value={null}>
      <div data-page-detail>
        <div data-page-detail-back>
          <Link
            to={backTo}
            className="-ml-1.5 inline-flex items-center gap-1.5 rounded-md py-1 pr-2 pl-1.5 text-xs font-medium text-gray-500 dark:text-muted-foreground hover:bg-gray-100 dark:hover:bg-muted hover:text-gray-900 dark:hover:text-foreground"
          >
            <ArrowLeft className="size-3.5" aria-hidden="true" />
            {backLabel}
          </Link>
        </div>
        {children}
      </div>
    </DetailBaseContext.Provider>
  );
}
